import React, { useState } from "react";
import { Send, MapPin, Navigation, Phone } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { useContent } from "../context/ContentContext";
import { external } from "../lib/links";

const directionsUrl = (address) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
const mapEmbed = (address) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

export const Contact = () => {
  const { content } = useContent();
  const c = content.contact;
  const office = c.office || {};
  // Combine the four structured fields into a single string for display,
  // map, and directions. Falls back to any legacy free-text `address` for
  // installs that haven't migrated yet.
  const addressForMap = [
    office.streetAddress,
    office.city,
    [office.region, office.postalCode].filter(Boolean).join(" "),
  ]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(", ") || office.address || "";
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success("Thank you for reaching out", {
      description: "This is a preview form. Email delivery will be connected soon — for now, please book a free consult.",
    });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-28 bg-cream-2/50">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal><p className="text-xs md:text-sm uppercase tracking-[0.24em] font-semibold text-ocean">{c.eyebrow}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">{c.heading}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-md">{c.body}</p></Reveal>

            <div className="mt-10 space-y-5">
              <Reveal delay={0.14}>
                <div>
                  <p className="text-ink font-medium">{c.office.name}</p>
                  {c.office.phone && (
                    <a data-testid="contact-phone-link" href={`tel:${c.office.phone.replace(/[^\d+]/g, "")}`} className="mt-0.5 flex items-center gap-1.5 text-ink-soft hover:text-sage-dark transition-colors duration-300">
                      <Phone className="h-3.5 w-3.5 shrink-0" /> {c.office.phone}
                    </a>
                  )}
                  <p className="mt-0.5 flex items-start gap-1.5 text-ink-soft">
                    <MapPin className="h-3.5 w-3.5 mt-1 shrink-0" />
                    <span>{addressForMap || office.address}</span>
                  </p>
                  <a data-testid="contact-directions-link" href={directionsUrl(addressForMap)} {...external} className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-dark hover:text-sage transition-colors duration-300">
                    <Navigation className="h-3.5 w-3.5" /> Get directions
                  </a>
                </div>
              </Reveal>

              <Reveal delay={0.18}>
                <div className="rounded-2xl overflow-hidden border border-cream-2 shadow-[0_18px_36px_-26px_rgba(122,143,119,0.45)]">
                  <iframe title="Office location map" data-testid="office-map" src={mapEmbed(addressForMap)} width="100%" height="240" style={{ border: 0, display: "block" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.12}>
            <form data-testid="contact-form" onSubmit={handleSubmit} className="bg-white rounded-[2rem] p-8 md:p-10 shadow-[0_24px_48px_-28px_rgba(122,143,119,0.4)] border border-cream-2">
              <div className="space-y-8">
                {[
                  { name: "name", label: "Your name", type: "text", testid: "contact-name" },
                  { name: "email", label: "Email address", type: "email", testid: "contact-email" },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2" htmlFor={f.name}>{f.label}</label>
                    <input id={f.name} data-testid={f.testid} type={f.type} required value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="w-full bg-transparent border-b border-sand focus:border-sage-dark outline-none py-2.5 text-ink text-lg transition-colors duration-300" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-ink-muted mb-2" htmlFor="message">Message</label>
                  <textarea id="message" data-testid="contact-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b border-sand focus:border-sage-dark outline-none py-2.5 text-ink text-lg resize-none transition-colors duration-300" />
                </div>
                <button data-testid="contact-submit-btn" type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-cream px-8 py-4 font-semibold transition-[transform,background-color] duration-300 hover:bg-sage-dark hover:-translate-y-0.5">
                  {c.submitLabel} <Send className="h-4 w-4" />
                </button>
                <p className="text-center text-xs text-ink-muted">{c.formNote}</p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
